'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@eliadmin/core/src/components/ui/alert-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useEffect } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const AGE_VERIFICATION_COOKIE = 'age-verified';
/** Once confirmed we don't want to ask again, so we keep it for a year. */
const AGE_VERIFICATION_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function hasConfirmedAge() {
  return document.cookie.split('; ').some(cookie => cookie === `${AGE_VERIFICATION_COOKIE}=true`);
}

/**
 * Blocking age-confirmation dialog for the alcohol e-shop section.
 * Only mount this inside e-shop/category/product trees, not on presentation pages.
 */
export function AgeVerificationGate() {
  const { t } = useTranslations();
  const { isOpen, close, open } = useDisclosure();
  const generalLayout = useGlobalStore(state => state.generalLayout);

  useEffect(() => {
    if (!hasConfirmedAge()) {
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = () => {
    document.cookie = `${AGE_VERIFICATION_COOKIE}=true; max-age=${AGE_VERIFICATION_MAX_AGE_SECONDS}; path=/`;
    close();
  };

  const handleDecline = () => {
    window.location.href = ROUTES.HOME();
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent className="max-w-xl">
        <div className="flex flex-col items-center gap-4 text-center">
          {!!generalLayout.header?.logo && (
            <Image
              src={generalLayout.header?.logo?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(generalLayout?.header?.logo, undefined)}
              width="0"
              height="0"
              sizes={buildImageSizes([{ size: '200px' }])}
              className="h-auto max-h-[100px] w-full max-w-[200px]"
            />
          )}
          <AlertDialogTitle className="text-lg font-bold sm:text-2xl">
            Overenie veku
          </AlertDialogTitle>
          <AlertDialogDescription className="text-base text-foreground">
            Táto časť webu ponúka na predaj alkoholické nápoje. Vstup je povolený iba osobám starším
            ako <span className="font-bold">18 rokov</span>.
          </AlertDialogDescription>
        </div>
        <AlertDialogFooter className="w-full items-center justify-center gap-2 sm:flex-row sm:justify-center">
          <Button
            onClick={handleDecline}
            variant={'none'}
            className="w-full border border-border text-black"
          >
            Nemám 18 rokov
          </Button>
          <AlertDialogAction onClick={handleConfirm} className="w-full">
            {t('text841')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
